type CentsValue = bigint | number | null | undefined

interface CustomerFacingPaymentTotalsInput {
  amountPaidCents: CentsValue
  amountDueCents: CentsValue
  tipAccountingVersion?: number | null
  tipPaidCents?: CentsValue
  tipDueCents?: CentsValue
  /**
   * Total tip on the invoice (the current revision's tip). Only consulted for
   * legacy (v0) invoices to reconstruct how much tip was collected. Omit it and
   * v0 invoices fall back to base-only paid/due (the prior behavior).
   */
  tipCents?: CentsValue
}

function centsToNumber(value: CentsValue): number {
  return Number(value ?? 0)
}

export function getCustomerFacingPaymentTotals(input: CustomerFacingPaymentTotalsInput) {
  const amountPaidCents = centsToNumber(input.amountPaidCents)
  const amountDueCents = centsToNumber(input.amountDueCents)
  const isTipAccountingV1 = (input.tipAccountingVersion ?? 0) >= 1

  if (isTipAccountingV1) {
    // Explicit tip accounting: tip paid/due are tracked directly on the invoice.
    const tipPaidCents = centsToNumber(input.tipPaidCents)
    const tipDueCents = centsToNumber(input.tipDueCents)

    return {
      paidCents: amountPaidCents + tipPaidCents,
      dueCents: amountDueCents + tipDueCents,
      tipPaidCents,
      tipDueCents,
    }
  }

  // Legacy (v0) invoices never split the collected tip into tip_paid_cents — it
  // stays 0 even though the customer was charged base + tip together. Reconstruct
  // the paid tip in proportion to base-payment progress so the displayed totals
  // and the payment progress bar reflect the tip that was actually collected
  // (otherwise a fully paid, tipped invoice reads under 100%). With no tip on the
  // invoice (or no tipCents provided) this is a no-op: paid/due fall back to the
  // base-only values. amountDueCents already nets out goodwill refunds, so the
  // base fraction stays correct for partially-refunded invoices too.
  const totalTipCents = centsToNumber(input.tipCents)
  const baseTotalCents = amountPaidCents + amountDueCents
  const baseFraction =
    baseTotalCents > 0 ? Math.min(amountPaidCents / baseTotalCents, 1) : amountPaidCents > 0 ? 1 : 0
  const tipPaidCents = Math.round(totalTipCents * baseFraction)
  const tipDueCents = Math.max(0, totalTipCents - tipPaidCents)

  return {
    paidCents: amountPaidCents + tipPaidCents,
    dueCents: amountDueCents + tipDueCents,
    tipPaidCents,
    tipDueCents,
  }
}
